import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Dispatch, type SetStateAction, useCallback } from "react";

import { toast } from "@performyard/py-components";
import type {
	ApproveBudgetData,
	BudgetDecisionData,
	BudgetReviewsResponse,
	CompensationCSVRequestModel,
	CompensationCSVResponse,
	CompensationCycleListResponse,
	CompensationCycleResponse,
	CompensationDecisionsWidgetResponse,
	CompensationSettingsResponse,
	CompleteCompensationCycleResponse,
	CreateCompensationCycleRequest,
	DeleteCompensationCycleResponse,
	GetCycleUsersWithoutCompensationDataResponse,
	GetSelectionManagersResponse,
	GetUsersWithoutCompensationDataResponse,
	MyDecisionsResponse,
	SelectionSalarySummaryResponse,
	SubmitDecisionsRequest,
	UpdateCompensationCycleRequest,
	UpdateMyDecisionsNotesRequest,
} from "@performyard/service-contracts";
import type { ConvertObjectIdsToString } from "@performyard/utils-isomorphic";

import { IncreaseValues } from "react/pages/Compensation/CycleDetails/MyDecisions/types";
import { DecisionRow } from "react/pages/Compensation/types";

import {
	compensationCycleBudgetReviewDataQuery,
	compensationCycleDetailsQuery,
	compensationCycleListQuery,
	compensationCycleListWithMyDecisionsQuery,
	compensationDecisionsWidgetQuery,
	compensationMyDecisionDetailsQuery,
	compensationSettingsQuery,
	getCycleHasUsersWithoutCompensationDataQuery,
	getUsersWithoutCompensationDataQuery,
	selectionManagersQuery,
	selectionSalarySummaryQuery,
} from "../../queries";
import {
	addCompensationCycleOwner,
	approveBudget,
	bulkApproveBudget,
	completeCompensationCycle,
	createCompensationCycle,
	deleteCompensationCycle,
	dismissSalaryFieldVisibilityConflictBanner,
	downloadCompensationCycleCSV,
	editBudgetDecision,
	getCompanySalaryCustomFieldData,
	getCompanySalaryCustomFieldId,
	getUserSalaryBannerDismissalData,
	removeCompensationCycleOwner,
	reopenCompensationCycle,
	submitDecisions,
	updateActingManager,
	updateCompensationCycleDetails,
	updateCompensationCycleOwners,
	updateMyDecisionsNotes,
} from "../../services/defs/internal-api/compensation";
import type { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

type MyDecisionsDetailsData = ConvertObjectIdsToString<MyDecisionsResponse>;

export const useCompensationSettings = () => {
	return useQuery<CompensationSettingsResponse, Error>(compensationSettingsQuery());
};

type CycleOwnersMutationInput = {
	cycleOwnerIds: string[];
	/** Hint for optimistic UI when adding a new owner */
	newOwner?: { _id: string; name: string };
};

export const useUpdateCompensationCycleOwners = () => {
	const queryClient = useQueryClient();
	const queryKey = ["compensation-settings"];

	return useMutation({
		mutationKey: ["compensation-settings", "cycle-owners"],
		mutationFn: async ({ cycleOwnerIds }: CycleOwnersMutationInput) => {
			return updateCompensationCycleOwners(cycleOwnerIds);
		},
		onMutate: async ({ cycleOwnerIds, newOwner }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<CompensationSettingsResponse>(queryKey);

			if (previous) {
				const existingById = new Map(previous.cycleOwners.map((o) => [o._id, o]));
				// Keep all admin entries at the beginning (they aren't in cycleOwnerIds since they're auto-included)
				const adminOwners = previous.cycleOwners.filter((o) => o.isAdmin);
				const explicitOwners = cycleOwnerIds.map((id) => {
					const existing = existingById.get(id);
					if (existing) return existing;
					if (newOwner && newOwner._id === id) {
						return { _id: id, name: newOwner.name, role: "Loading..." as const, isAdmin: false };
					}
					return { _id: id, name: "", role: "Loading..." as const, isAdmin: false };
				});
				// Merge admins first, then explicit owners (no client-side sorting)
				const explicitIdSet = new Set(explicitOwners.map((o) => o._id));
				const merged = [...adminOwners.filter((o) => !explicitIdSet.has(o._id)), ...explicitOwners];

				queryClient.setQueryData<CompensationSettingsResponse>(queryKey, {
					...previous,
					cycleOwners: merged as CompensationSettingsResponse["cycleOwners"],
				});
			}

			return { previous };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
		onError: (_error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
	});
};

export const useSelectionManagers = (
	userSelectionId: string,
	params?: {
		skip?: number;
		limit?: number;
		sortBy?: "name" | "directReportCount" | "totalCompensation";
		sortOrder?: "asc" | "desc";
		search?: string;
	},
	options?: PYUseQueryOptions<GetSelectionManagersResponse>
) => {
	return useQuery<GetSelectionManagersResponse, Error>({
		...selectionManagersQuery(userSelectionId, params),
		enabled: !!userSelectionId,
		...options,
	});
};

export const useSelectionSalarySummary = (
	userSelectionId: string,
	options?: PYUseQueryOptions<SelectionSalarySummaryResponse>
) => {
	return useQuery<SelectionSalarySummaryResponse, Error>({
		...selectionSalarySummaryQuery(userSelectionId),
		enabled: !!userSelectionId,
		...options,
	});
};

export const useCreateCompensationCycle = (
	options?: PYUseMutationOptions<CompensationCycleResponse, Error, CreateCompensationCycleRequest>
) => {
	return useMutation({
		mutationKey: ["compensation-cycles", "create"],
		mutationFn: (data: CreateCompensationCycleRequest) => createCompensationCycle(data),
		meta: {
			errorMessage: "There was an error creating the compensation cycle.",
		},
		...options,
	});
};

// Admins and Cycle Owners can view all compensation cycles
export const useCompensationCycleList = (
	params: {
		skip?: number;
		limit?: number;
		sort?: string;
		search?: string;
		status?: string;
	},
	options?: PYUseQueryOptions<CompensationCycleListResponse>
) => {
	return useQuery<CompensationCycleListResponse, Error>({
		...compensationCycleListQuery(params),
		...options,
	});
};

// managers/acting managers can only view compensation cycles in which they have decisions to make
export const useCompensationCycleListWithMyDecisions = (
	params: {
		skip?: number;
		limit?: number;
		sort?: string;
		search?: string;
		status?: string;
	},
	options?: PYUseQueryOptions<CompensationCycleListResponse>
) => {
	return useQuery<CompensationCycleListResponse, Error>({
		...compensationCycleListWithMyDecisionsQuery(params),
		...options,
	});
};

// Dashboard widget: my Active/Overdue cycles with an unsubmitted batch.
export const useCompensationDecisionsWidget = (options?: PYUseQueryOptions<CompensationDecisionsWidgetResponse>) => {
	return useQuery<CompensationDecisionsWidgetResponse, Error>({
		...compensationDecisionsWidgetQuery(),
		retry: false,
		...options,
	});
};

export const useDeleteCompensationCycle = () => {
	const queryClient = useQueryClient();

	return useMutation<DeleteCompensationCycleResponse, Error, string>({
		mutationKey: ["compensation-cycles", "delete"],
		mutationFn: (cycleId: string) => deleteCompensationCycle(cycleId),
		meta: {
			errorMessage: "There was an error deleting the compensation cycle.",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["compensation-cycles"] });
			toast.success("Cycle has been deleted");
		},
	});
};

type AddCycleOwnerMutationInput = {
	userId: string;
	userName: string;
};

export const useAddCompensationCycleOwner = () => {
	const queryClient = useQueryClient();
	const queryKey = ["compensation-settings"];

	return useMutation({
		mutationKey: ["compensation-settings", "add-cycle-owner"],
		mutationFn: async ({ userId }: AddCycleOwnerMutationInput) => {
			return addCompensationCycleOwner(userId);
		},
		onMutate: async ({ userId, userName }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<CompensationSettingsResponse>(queryKey);

			if (previous) {
				const exists = previous.cycleOwners.some((o) => o._id === userId);
				if (exists) return { previous };

				const newOwner = {
					_id: userId,
					name: userName,
					role: "Loading..." as const,
					isAdmin: false,
				};

				// Append to end - backend will re-sort by role on success
				const updatedOwners = [...previous.cycleOwners, newOwner];

				queryClient.setQueryData<CompensationSettingsResponse>(queryKey, {
					...previous,
					cycleOwners: updatedOwners,
				});
			}

			return { previous };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
		onError: (_error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
	});
};

type RemoveCycleOwnerMutationInput = {
	userId: string;
};

export const useRemoveCompensationCycleOwner = () => {
	const queryClient = useQueryClient();
	const queryKey = ["compensation-settings"];

	return useMutation({
		mutationKey: ["compensation-settings", "remove-cycle-owner"],
		mutationFn: async ({ userId }: RemoveCycleOwnerMutationInput) => {
			return removeCompensationCycleOwner(userId);
		},
		onMutate: async ({ userId }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<CompensationSettingsResponse>(queryKey);

			if (previous) {
				const updatedOwners = previous.cycleOwners.filter((o) => o._id !== userId);

				queryClient.setQueryData<CompensationSettingsResponse>(queryKey, {
					...previous,
					cycleOwners: updatedOwners,
				});
			}

			return { previous };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
		onError: (_error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
	});
};

export const useCompensationCycleDetails = (
	cycleId: string,
	options?: PYUseQueryOptions<CompensationCycleResponse>
) => {
	return useQuery<ConvertObjectIdsToString<CompensationCycleResponse>, Error>({
		...compensationCycleDetailsQuery(cycleId),
		...options,
	});
};

export const useMyDecisionDetails = (
	userId: string,
	cycleId: string,
	options?: PYUseQueryOptions<MyDecisionsDetailsData>
) => {
	return useQuery<MyDecisionsDetailsData, Error>({
		...compensationMyDecisionDetailsQuery(userId, cycleId),
		...options,
	});
};
export const useUsersWithoutCompensationData = () => {
	return useQuery<GetUsersWithoutCompensationDataResponse>(getUsersWithoutCompensationDataQuery());
};

export const useCompanySalaryCustomFieldData = () => {
	return useQuery({
		queryKey: ["company-salary-custom-field"],
		queryFn: () => getCompanySalaryCustomFieldData(),
		meta: {
			errorMessage: "There was an error getting the company fields.",
		},
	});
};

export const useUserBannerDismissalData = () => {
	return useQuery({
		queryKey: ["user-salary-vis-conflict-banner-dismissal-data"],
		queryFn: () => getUserSalaryBannerDismissalData(),
		meta: {
			errorMessage: "There was an error getting the company fields.",
		},
	});
};

export const useDismissSalaryFieldVisibilityConflictBanner = () => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: dismissSalaryFieldVisibilityConflictBanner,
		onSuccess: () => {
			client.invalidateQueries({ queryKey: ["user-salary-vis-conflict-banner-dismissal-data"] });
		},
	});
};

export const useUpdateMyDecisionsNotes = () => {
	return useMutation({
		mutationKey: ["compensation", "my-decisions-notes", "update"],
		mutationFn: (data: UpdateMyDecisionsNotesRequest) => updateMyDecisionsNotes(data),
		meta: {
			errorMessage: "There was an error saving decision notes.",
		},
	});
};

export const useSubmitDecisions = () => {
	const queryClient = useQueryClient();
	const queryKey = ["compensation-decision"];

	return useMutation({
		mutationKey: ["compensation-decision"],
		mutationFn: async (data: SubmitDecisionsRequest) => {
			return submitDecisions(data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
};

const mapDecisionRowsToSubmitPayload = (rows: DecisionRow[], cycleIdFallback: string): SubmitDecisionsRequest =>
	rows
		.filter((row) => row.superRow !== true)
		.map((row) => {
			const proposed_increase_amount = row.proposed_increase_amount;
			const proposed_increase_percent = row.proposed_increase_percent;
			if (proposed_increase_amount == null || proposed_increase_percent == null) {
				throw new Error("Submit called with decision rows missing increase amount or percent");
			}
			return {
				_id: row._id,
				current_salary: row.current_salary,
				employee_id: row.employee_id,
				notes: row.notes ?? "",
				proposed_increase_amount,
				proposed_increase_percent,
				proposed_salary: row.proposed_salary ?? row.current_salary,
				compensation_cycle_id: row.compensation_cycle_id ?? cycleIdFallback,
			};
		});

type SubmitDecisionsMutate = (
	variables: SubmitDecisionsRequest,
	options?: {
		onSuccess?: () => void | Promise<void>;
		onError?: () => void;
	}
) => void;

export type UseDecisionSubmitParams = {
	cycleId: string | undefined;
	userID: string | undefined;
	groupDecisionRowsByManagerId: Record<string, DecisionRow[]>;
	submitDecisions: SubmitDecisionsMutate;
	setGroupCannotBeSubmitted: Dispatch<SetStateAction<string | null>>;
	setRefreshingGroupIds: Dispatch<SetStateAction<Record<string, boolean>>>;
	setDraftOverrides: Dispatch<SetStateAction<IncreaseValues>>;
};

export const useDecisionSubmit = ({
	cycleId,
	userID,
	groupDecisionRowsByManagerId,
	submitDecisions: submitDecisionsMutate,
	setGroupCannotBeSubmitted,
	setRefreshingGroupIds,
	setDraftOverrides,
}: UseDecisionSubmitParams): ((managerId: string) => void) => {
	const queryClient = useQueryClient();

	return useCallback(
		(managerId: string) => {
			setGroupCannotBeSubmitted(null);
			const groupRows = groupDecisionRowsByManagerId[managerId] ?? [];
			if (!userID || !cycleId) {
				toast.error("Error occurred while submitting decisions.");
				return;
			}
			setRefreshingGroupIds((prev) => ({ ...prev, [managerId]: true }));
			submitDecisionsMutate(mapDecisionRowsToSubmitPayload(groupRows, cycleId), {
				onSuccess: async () => {
					await queryClient.invalidateQueries({
						queryKey: compensationMyDecisionDetailsQuery(userID, cycleId).queryKey,
					});

					const submittedRowIds = new Set(groupRows.map((row) => row._id));
					setDraftOverrides(
						(prev) =>
							Object.fromEntries(
								Object.entries(prev).filter(([rowId]) => !submittedRowIds.has(rowId))
							) as IncreaseValues
					);
					setRefreshingGroupIds((prev) => ({ ...prev, [managerId]: false }));
					toast.success("Your decisions have been submitted for approval");
				},
				onError: async () => {
					setRefreshingGroupIds((prev) => ({ ...prev, [managerId]: false }));
					await queryClient.invalidateQueries({
						queryKey: compensationMyDecisionDetailsQuery(userID, cycleId).queryKey,
					});
				},
			});
		},
		[
			cycleId,
			groupDecisionRowsByManagerId,
			queryClient,
			setDraftOverrides,
			setGroupCannotBeSubmitted,
			setRefreshingGroupIds,
			submitDecisionsMutate,
			userID,
		]
	);
};

export const useCycleBudgetReviewDetails = (cycleId: string, options?: PYUseQueryOptions<BudgetReviewsResponse>) => {
	const { enabled: enabledOption, ...restOptions } = options ?? {};
	return useQuery<BudgetReviewsResponse, Error>({
		...compensationCycleBudgetReviewDataQuery(cycleId),
		...restOptions,
		enabled: !!cycleId && (enabledOption ?? true),
	});
};

export const useAssignActingManager = (cycleId: string) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["compensation", "budget-reviews", "update-acting-manager", cycleId],
		mutationFn: ({ currentManagerId, actingManagerId }: { currentManagerId: string; actingManagerId: string }) =>
			updateActingManager(cycleId, currentManagerId, actingManagerId),
		onSuccess: () => {
			if (cycleId) {
				queryClient.invalidateQueries({
					queryKey: compensationCycleBudgetReviewDataQuery(cycleId).queryKey,
				});
			}
		},
		meta: {
			errorMessage: "There was an error assigning an acting manager",
		},
	});
};

export const useApproveBudget = (cycleId: string, approverId: string, budgetData: ApproveBudgetData) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["compensation", "approve-budget", cycleId],
		mutationFn: () => approveBudget(cycleId, approverId, budgetData),
		onSuccess: () => {
			if (cycleId) {
				queryClient.invalidateQueries({
					queryKey: compensationCycleBudgetReviewDataQuery(cycleId).queryKey,
				});
			}
			toast.success("Budget Approved");
		},
		meta: {
			errorMessage: "There was an error approving the budget.",
		},
	});
};

export const useCycleOwnerEditBudgetDecision = (cycleId: string, budgetDecisionData: BudgetDecisionData) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["compensation", "edit-budget-decision", cycleId],
		mutationFn: () => editBudgetDecision(cycleId, budgetDecisionData),
		onSuccess: () => {
			if (cycleId) {
				queryClient.invalidateQueries({
					queryKey: compensationCycleBudgetReviewDataQuery(cycleId).queryKey,
				});
			}
		},
		meta: {
			errorMessage: "There was an error editing the budget.",
		},
	});
};

export const useBulkApproveBudget = (cycleId: string, approverId: string, budgetData: ApproveBudgetData[]) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["compensation", "bulk-approve-budgets", cycleId],
		mutationFn: async () => {
			await bulkApproveBudget(cycleId, approverId, budgetData);
			if (cycleId) {
				await queryClient.invalidateQueries({
					queryKey: compensationCycleBudgetReviewDataQuery(cycleId).queryKey,
				});
			}
		},
		onSuccess: () => {
			toast.success(
				`${budgetData?.length} ${budgetData?.length === 1 ? "budget" : "budgets"} ${budgetData?.length > 1 ? "have" : "has"} been approved`
			);
		},
		meta: {
			errorMessage: "There was an error approving the budget.",
		},
	});
};

export const useCompleteCompensationCycle = () => {
	const queryClient = useQueryClient();

	return useMutation<CompleteCompensationCycleResponse, Error, string>({
		mutationKey: ["compensation-cycles", "complete"],
		mutationFn: (cycleId: string) => completeCompensationCycle(cycleId),
		meta: {
			errorMessage: "There was an error completing the compensation cycle.",
		},
		onSuccess: (_data, cycleId) => {
			queryClient.invalidateQueries({ queryKey: ["compensation-cycles"] });
			queryClient.invalidateQueries({ queryKey: ["compensation-cycle-details", cycleId] });
			toast.success("Compensation cycle has been marked completed");
		},
	});
};

export const useReopenCompensationCycle = () => {
	const queryClient = useQueryClient();

	return useMutation<CompleteCompensationCycleResponse, Error, string>({
		mutationKey: ["compensation-cycles", "reopen"],
		mutationFn: (cycleId: string) => reopenCompensationCycle(cycleId),
		meta: {
			errorMessage: "There was an error reopening the compensation cycle.",
		},
		onSuccess: (_data, cycleId) => {
			queryClient.invalidateQueries({ queryKey: ["compensation-cycles"] });
			queryClient.invalidateQueries({ queryKey: ["compensation-cycle-details", cycleId] });
			toast.success("Compensation cycle has been reopened");
		},
	});
};

export const useUpdateCompensationCycleDetails = () => {
	const queryClient = useQueryClient();

	return useMutation<
		CompleteCompensationCycleResponse,
		Error,
		{ cycleId: string; data: UpdateCompensationCycleRequest }
	>({
		mutationKey: ["compensation-cycles", "update", "details"],
		mutationFn: ({ cycleId, data }) => updateCompensationCycleDetails(cycleId, data),
		meta: {
			errorMessage: "There was an error updating the compensation cycle details.",
		},
		onSuccess: (_data, { cycleId }) => {
			queryClient.invalidateQueries({ queryKey: ["compensation-cycles"] });
			queryClient.invalidateQueries({ queryKey: ["compensation-cycle-details", cycleId] });
		},
	});
};

export const useDownloadCompensationCSV = (
	cycleId: string,
	options?: PYUseMutationOptions<CompensationCSVResponse, Error, CompensationCSVRequestModel>
) => {
	return useMutation({
		mutationFn: async (data: CompensationCSVRequestModel) => await downloadCompensationCycleCSV(cycleId, data),
		...options,
	});
};

export const useCompanySalaryFieldId = () => {
	return useQuery({
		queryKey: ["company-salary-custom-field-id"],
		queryFn: () => getCompanySalaryCustomFieldId(),
		meta: {
			errorMessage: "There was an error getting the company salary field.",
		},
	});
};
export const useCycleHasUsersWithoutCompensationData = (cycleId: string) => {
	return useQuery<GetCycleUsersWithoutCompensationDataResponse>(
		getCycleHasUsersWithoutCompensationDataQuery(cycleId)
	);
};
